import BigNumber from "bignumber.js";
import { BASE18 } from "src/configs/constance";
import heuristicVaultsConfig from "src/configs/heuristic-vaults";
import NFTHeuristicContract from "src/contract-logic/logic/FarmingBaseEXP";
import NFTCollectionContract from "src/contract-logic/logic/NFTCollectionContract";
import NFTCoreContract from "src/contract-logic/logic/NFTCoreContract";
import { RarityMapping } from "src/utils/knight";
import multiCall from "src/utils/multicall";
import { TravaNFTApiQuery } from "src/utils/query/api-query";

export default class FramingQuery {
  static async fetchHeuristicVault(vaultId, accountAddress) {
    const vault = heuristicVaultsConfig[vaultId];
    if (vault.disabled) throw new Error("vault disabled");
    const [idList, poolInfos, eps] = await Promise.all([
      TravaNFTApiQuery.get("/v1/heuristicfarming", { params: { owner: accountAddress, vaultType: vault.rarity } }),
      NFTHeuristicContract.poolInfos(vault.level),
      NFTHeuristicContract.getEmissionPerSecond(vault.level),
    ]);
    const dailyReward = BigNumber(eps)
      .multipliedBy(24 * 3600)
      .dividedBy(BASE18)
      .toNumber();
    const numberKnightOfUser = idList.length;
    const totalNFTs = poolInfos.nftCount;
    const totalVaultValue = Number(poolInfos.totalValue);
    const totalRewardOfUser = await NFTHeuristicContract.getTotalRewardsBalance(idList);
    const [nftInformation, expEarned, exp, balance] = await Promise.all([
      multiCall(
        NFTHeuristicContract.ABI,
        idList.map((id, _) => ({
          address: NFTHeuristicContract.ADDRESS,
          name: "nftInfos",
          params: [id],
        }))
      ),
      multiCall(
        NFTHeuristicContract.ABI,
        idList.map((id, _) => ({
          address: NFTHeuristicContract.ADDRESS,
          name: "getExpEarnedAndValueEarned",
          params: [id],
        }))
      ),
      multiCall(
        NFTCollectionContract.ABI,
        idList.map((id, _) => ({
          address: NFTCollectionContract.ADDRESS,
          name: "getCollectionExperience",
          params: [id],
        }))
      ),
      multiCall(
        NFTHeuristicContract.ABI,
        idList.map((id, _) => ({
          address: NFTHeuristicContract.ADDRESS,
          name: "getTotalRewardsBalance",
          params: [[id]],
        }))
      ),
    ]);
    const nftInfos = [];
    nftInformation.forEach((data, index) => {
      nftInfos.push({
        attainedExp: parseInt(exp[index][0]),
        depositTime: parseInt(data["depositTime"]) * 1000,
        id: Number(idList[index]),
        exp: Number(expEarned[index][0]),
        earn: BigNumber(balance[index]).dividedBy(BASE18).toNumber(),
        value: Number(expEarned[index][1]),
      });
    });
    const collectionMetadataArray = await Promise.all(
      idList.map(async (id, index) => {
        const collectionMetadata = await NFTCollectionContract.getCollectionMetadata(id);
        const itemIdList = [
          collectionMetadata.armorTokenId,
          collectionMetadata.helmetTokenId,
          collectionMetadata.shieldTokenId,
          collectionMetadata.weaponTokenId,
        ];
        const [itemsMetadata] = await Promise.all([
          multiCall(
            NFTCoreContract.ABI,
            itemIdList.map((tokenId) => ({
              address: NFTCoreContract.ADDRESS,
              name: "getTokenMetadata",
              params: [tokenId],
            }))
          ),
        ]);
        let armorMetadata = itemsMetadata[0];
        let helmetMetadata = itemsMetadata[1];
        let shieldMetadata = itemsMetadata[2];
        let weaponMetadata = itemsMetadata[3];
        const rarityStr = RarityMapping[collectionMetadata.rarity - 1];
        const price = heuristicVaultsConfig[`${rarityStr}-vault`].collectionPrice;
        return {
          armorMetadata,
          helmetMetadata,
          shieldMetadata,
          weaponMetadata,
          collectionMetadata,
          apr: ((((dailyReward * Number(nftInformation[index]["value"])) / totalVaultValue) * 365) / price) * 100,
        };
      })
    );

    const data = {
      ...vault,
      aprAvg: ((dailyReward * 365) / totalNFTs / vault.collectionPrice) * 100,
      numberKnightOfUser,
      totalNFTs,
      totalRewardOfUser: Number(totalRewardOfUser / BASE18),
      collection: nftInfos.map((data, index) => ({
        ...data,
        apr: collectionMetadataArray[index].apr,
        rarity: collectionMetadataArray[index].collectionMetadata.rarity,
        armor: {
          tokenId: collectionMetadataArray[index].collectionMetadata.armorTokenId,
          rarity: parseInt(collectionMetadataArray[index].armorMetadata[0].tokenRarity),
          exp: parseInt(collectionMetadataArray[index].armorMetadata[0].experiencePoint),
        },
        helmet: {
          tokenId: collectionMetadataArray[index].collectionMetadata.helmetTokenId,
          rarity: parseInt(collectionMetadataArray[index].helmetMetadata[0].tokenRarity),
          exp: parseInt(collectionMetadataArray[index].helmetMetadata[0].experiencePoint),
        },
        shield: {
          tokenId: collectionMetadataArray[index].collectionMetadata.shieldTokenId,
          rarity: parseInt(collectionMetadataArray[index].shieldMetadata[0].tokenRarity),
          exp: parseInt(collectionMetadataArray[index].shieldMetadata[0].experiencePoint),
        },
        weapon: {
          tokenId: collectionMetadataArray[index].collectionMetadata.weaponTokenId,
          rarity: parseInt(collectionMetadataArray[index].weaponMetadata[0].tokenRarity),
          exp: parseInt(collectionMetadataArray[index].weaponMetadata[0].experiencePoint),
        },
      })),
    };
    return data;
  }
}
